import { RouteProp, useIsFocused } from '@react-navigation/native';
import React, { useEffect, useMemo } from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import useMedicalProfiles from '../../../../../hooks/api/useMedicalProfiles';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { useCollaborators } from '../../../../../hooks/api/useCollaborators';
import { AlertIcon } from '../../../../v2BaseComponents/Icon';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import MaxWidthRestrictedContent from '../../../../v2BaseComponents/MaxWidthRestrictedContent';
import getName from '../../../../../utils/getName';
import Button from '../../../../v2BaseComponents/Button';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';

const intl = getIntl('org-invite-accept-success-screen', {
    success: 'Success!',
    subtext: "is now a collaborator and has access to {name}'s Medical Profile.",
    viewCollaborators: 'View Collaborators',
});

export type OrgInviteAcceptSuccessScreenOwnProps = {};

export type OrgInviteAcceptSuccessScreenProps = OrgInviteAcceptSuccessScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'org-invite-accept-success'>;
};

const OrgInviteAcceptSuccessScreenScreen: React.FC<OrgInviteAcceptSuccessScreenProps> = (props) => {
    const isFocused = useIsFocused();
    const [delayPassed, setDelayPassed] = React.useState(false);
    useEffect(() => {
        if (isFocused) {
            setTimeout(() => {
                setDelayPassed(true);
            }, 1000);
        }
    }, [isFocused]);

    const navigator = useProfilesTabNavigation();
    const isMobile = useIsMobileLayout();
    const { isLoadingProfiles, medicalProfiles, collaboratorMedicalProfiles } = useMedicalProfiles();
    const { isLoadingCollaborators, organizationCollaborators } = useCollaborators({
        medicalProfileId: props.route.params.clientProfileId,
    });

    const targetProfile = useMemo(() => {
        return [...medicalProfiles, ...collaboratorMedicalProfiles].find(
            (profile) => profile.medicalProfileId === props.route.params.clientProfileId,
        );
    }, [medicalProfiles, collaboratorMedicalProfiles, props.route.params.clientProfileId]);

    const organizationCollaborator = useMemo(
        () =>
            organizationCollaborators.find((_) => _.organization.organizationId === props.route.params.organizationId),
        [organizationCollaborators, props.route.params.organizationId],
    );

    useEffect(() => {
        if (!delayPassed) return;

        if (!isLoadingProfiles && !isLoadingCollaborators && (!targetProfile || !organizationCollaborator)) {
            navigator.navigate('root', {});
        }
    }, [isLoadingProfiles, isLoadingCollaborators, targetProfile, organizationCollaborator, navigator, delayPassed]);

    return (
        <AnimateLoadedContent
            isLoading={
                isLoadingProfiles ||
                isLoadingCollaborators ||
                !targetProfile ||
                !organizationCollaborator ||
                !delayPassed
            }
        >
            {() => (
                <ActionButtonLayout scrollable>
                    <MaxWidthRestrictedContent>
                        <MainContentContainer>
                            <Stack vertical fullWidth space={contentStackSpacing} align="center">
                                <AlertIcon variant="success" name="Check" />
                                <Text variant="body-1" alignText="center">
                                    {intl('success')}
                                </Text>
                                <Text variant="bold-body-1">
                                    {organizationCollaborator?.organization.metadata.name}
                                </Text>
                                <Text variant="body-1" alignText="center">
                                    {intl('subtext', {
                                        name: getName(targetProfile!),
                                    })}
                                </Text>
                                <Stack vertical fullWidth style={{ paddingTop: 20 }} align="center">
                                    <Button
                                        variant="primary"
                                        title={intl('viewCollaborators')}
                                        size="lg"
                                        onPress={() => {
                                            if (isMobile) {
                                                navigator.navigate('profile', {
                                                    profileId: targetProfile!.medicalProfileId,
                                                    defaultTab: 'manage',
                                                });
                                            } else {
                                                navigator.navigate('access', {
                                                    profileId: targetProfile!.medicalProfileId,
                                                });
                                            }
                                        }}
                                        testID="org-invite-accept-success-view-collaborators-button"
                                    />
                                </Stack>
                            </Stack>
                        </MainContentContainer>
                    </MaxWidthRestrictedContent>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(OrgInviteAcceptSuccessScreenScreen);
